import React from "react";

import * as types from "types";
import Message from "../Message";
import CompactionSummary from "./CompactionSummary";
import { prepMessagesForRendering } from "./messageUtils";
import styles from "./MessageHistory.module.scss";

interface MessageHistoryProps {
  messages: types.Message[];
  /**
   * Attached to the content wrapper so a parent's ResizeObserver can follow
   * content growth (streaming chunks, async markdown/diagram rendering).
   */
  contentRef?: React.Ref<HTMLDivElement>;
  /**
   * Chat Thread could be undefined for some experiments that don't save chat history.
   * Those pages don't support file uploads.
   */
  chatThread?: types.ChatThread;
  /**
   * When true, MermaidDiagram and VegaLiteChart code blocks defer rendering
   * until streaming ends so they don't run their parsers against partial
   * code. Optional — defaults to false (render immediately) for routes that
   * aren't streaming-aware.
   */
  isStreaming?: boolean;
  /**
   * Forwarded down to VegaLiteChart so it can resolve `file://<file_id>`
   * data URLs via the cc-file-api. Optional — file:// URLs won't load
   * when omitted.
   */
  getAccessToken?: () => Promise<string>;
  onAskAboutTask?: (prompt: string) => void;
  incognito?: boolean;
}

/**
 * Responsible for rendering the list of messages.
 */
export default function MessageHistory({
  chatThread,
  contentRef,
  messages,
  isStreaming,
  getAccessToken,
  onAskAboutTask,
  incognito,
}: MessageHistoryProps) {
  const messageListItems = React.useMemo(() => {
    const filteredMessages = prepMessagesForRendering(messages);

    return filteredMessages.map((msg) => {
      if (types.isCompactionProgressMessage(msg)) {
        return (
          <li
            key={msg.uuid}
            className={`mt4 mt3-ns ${styles.compactionIndicator}`}
          >
            <span>Summarizing conversation… This may take a moment.</span>
          </li>
        );
      }

      if (types.isCompactionMessage(msg)) {
        return (
          <li key={msg.uuid} className="mt4 mt3-ns">
            <CompactionSummary message={msg} />
          </li>
        );
      }

      return (
        <li key={msg.uuid} className="mt4 mt3-ns">
          <Message
            chatThread={chatThread}
            message={msg}
            isStreaming={isStreaming}
            getAccessToken={getAccessToken}
            onAskAboutTask={onAskAboutTask}
            incognito={incognito}
          />
        </li>
      );
    });
  }, [
    messages,
    chatThread,
    isStreaming,
    getAccessToken,
    onAskAboutTask,
    incognito,
  ]);

  const messageList = <ul className="list pl0 mt0">{messageListItems}</ul>;

  return <div ref={contentRef}>{messageList}</div>;
}
